import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import InfoTip from '../InfoTip';
import MachineFilter from './MachineFilter';
import { buildMachineCatalog } from '../../utils/machineFilters';
import {
    MACHINE_PILL_FIELDS, buildFilterPills, clearFilterField, countQualifyingQuotes, countRetarget,
    formatLocations, secondaryFilterCount,
} from '../../utils/inventoryView';
import { useDealershipSettings } from '../../contexts/settingsContext';
import './FilterControls.css';

function FilterControls({
    filters,
    onChange,
    allData,
    filteredCount,
    onQualifyingQuoteToggle,
    onRetargetingToggle,
    hasActiveFilters,
    onClearAllFilters,
    resultCountRef,
    taskFilterActive,
    openTaskCount,
    onTaskFilterToggle,
    machineFacetData,
    machineCatalog: machineCatalogProp,
    currentYear,
}) {
    const [showMore, setShowMore] = useState(false);
    // The dealership's aging buckets; the filter holds their ids (1–4).
    const { aging } = useDealershipSettings();

    // Focus targets after a pill is removed (the × that had focus is gone).
    const pillsRef = useRef(null);
    const machineButtonRef = useRef(null);
    const ownCountRef = useRef(null);
    const countRef = resultCountRef || ownCountRef;
    const pendingPillFocusRef = useRef(null);
    useEffect(() => {
        const pending = pendingPillFocusRef.current;
        if (!pending) return;
        pendingPillFocusRef.current = null;
        const buttons = pillsRef.current ? [...pillsRef.current.querySelectorAll('.pill-remove')] : [];
        if (buttons.length > 0) {
            buttons[Math.min(pending.index, buttons.length - 1)].focus();
        } else if (pending.machine) {
            machineButtonRef.current?.focus();
        } else {
            countRef.current?.focus();
        }
    });

    // Make / Model / Year options and labels, derived from the loaded units.
    const machineCatalog = useMemo(
        () => machineCatalogProp || buildMachineCatalog(allData),
        [machineCatalogProp, allData],
    );

    // Aging multi-select popover. Mirrors the Comps-tab state picker:
    // an anchored checkbox list where an empty selection = "All" (no filter).
    const [agingOpen, setAgingOpen] = useState(false);
    const agingFilterRef = useRef(null);
    const agingBtnRef = useRef(null);
    const selectedAging = filters.agingBucket || [];

    // Close the aging popover on outside click or Escape. The Escape handler
    // stops propagation so the first press only dismisses the popover (other
    // components also close on Escape).
    useEffect(() => {
        if (!agingOpen) return undefined;
        const closeAndRefocus = () => {
            setAgingOpen(false);
            agingBtnRef.current?.focus();
        };
        const onPointerDown = (event) => {
            if (agingFilterRef.current && !agingFilterRef.current.contains(event.target)) {
                closeAndRefocus();
            }
        };
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            closeAndRefocus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [agingOpen]);

    const toggleAging = (bucket) => {
        const current = filters.agingBucket || [];
        const next = current.includes(bucket)
            ? current.filter((b) => b !== bucket)
            : [...current, bucket];
        handleChange('agingBucket', next);
    };

    const locations = ['All', ...new Set(allData.map(u => u.BranchName).filter(Boolean))].sort();
    const categories = ['All', ...new Set(allData.map(u => u.CATEGORY).filter(Boolean))].sort();
    const newUsedOptions = ['All', ...new Set(allData.map(u => u.NewUsedStatus).filter(Boolean))].sort();
    const salesmen = ['All', ...new Set(allData.map(u => u.Salesman).filter(Boolean))].sort();
    const agingBuckets = aging.buckets;
    const sellSpeedOptions = ['All', 'Quick Sell', 'Normal', 'Slow', 'Critical'];
    const buyerHeatOptions = ['All', 'Hot', 'Warm', 'Cold', 'No Interest'];

    const handleChange = (field, value) => {
        onChange({ ...filters, [field]: value });
    };

    const qqCount = countQualifyingQuotes(allData);
    const retargetCount = countRetarget(allData);

    // Active filter pills (shared with the v2 page: utils/inventoryView).
    const activeFilters = buildFilterPills(filters, machineCatalog, aging.buckets);
    const secondaryCount = secondaryFilterCount(activeFilters);

    const removePill = (field, index) => {
        pendingPillFocusRef.current = { index, machine: MACHINE_PILL_FIELDS.includes(field) };
        onChange(clearFilterField(filters, field));
    };

    // Several locations can only come from the new design (saved prefs);
    // this select shows them as one entry instead of breaking.
    const multiLocation = Array.isArray(filters.location);

    return (
        <div className="filter-controls">
            {/* Primary filter row */}
            <div className="filter-primary">
                <div className="search-field">
                    <svg className="search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    <input
                        type="text"
                        id="search"
                        placeholder="Search stock #, make, model..."
                        value={filters.search}
                        onChange={(e) => handleChange('search', e.target.value)}
                    />
                </div>

                <div className="type-toggle" role="group" aria-label="New or Used filter">
                    {[{ value: 'All', label: 'All' }, { value: 'N', label: 'New' }, { value: 'U', label: 'Used' }].map(opt => (
                        <button
                            key={opt.value}
                            className={`type-toggle-btn ${filters.newUsed === opt.value ? 'active' : ''}`}
                            onClick={() => handleChange('newUsed', opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                <div className="filter-select">
                    <select
                        id="location"
                        value={multiLocation ? '__multi__' : filters.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        aria-label="Location"
                    >
                        <option value="All">All Locations</option>
                        {multiLocation && <option value="__multi__" disabled>{formatLocations(filters.location)}</option>}
                        {locations.filter(o => o !== 'All').map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-select">
                    <select
                        id="category"
                        value={filters.category}
                        onChange={(e) => handleChange('category', e.target.value)}
                        aria-label="Category"
                    >
                        <option value="All">All Categories</option>
                        {categories.filter(o => o !== 'All').map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>

                <MachineFilter
                    filters={filters}
                    onChange={onChange}
                    catalog={machineCatalog}
                    facetData={machineFacetData || allData}
                    currentYear={currentYear ?? new Date().getFullYear()}
                    resultCount={filteredCount}
                    buttonRef={machineButtonRef}
                />

                <button
                    className={`btn-more-filters ${showMore || secondaryCount > 0 ? 'has-filters' : ''}`}
                    onClick={() => setShowMore(!showMore)}
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                        <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                    </svg>
                    {showMore ? 'Less' : 'Filters'}
                    {secondaryCount > 0 && !showMore && (
                        <span className="filter-count-badge">{secondaryCount}</span>
                    )}
                </button>

                <button
                    className={`btn-qualifying-quote ${filters.qualifyingQuote ? 'active' : ''}`}
                    onClick={onQualifyingQuoteToggle}
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                    Live Quote ≥ AI Price
                    <InfoTip text={filters.qualifyingQuote
                        ? `Active — showing ${filteredCount.toLocaleString('en-US')} units. Click to turn off.`
                        : `${qqCount.toLocaleString('en-US')} units have an active quote at or above the AI estimated value. The buyer is already offering fair market price — call them before dropping the list price.`
                    } />
                </button>

                <button
                    className={`btn-qualifying-quote ${filters.retargeting ? 'active' : ''}`}
                    onClick={onRetargetingToggle}
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 110-12 6 6 0 010 12zm0-9a1 1 0 011 1v4a1 1 0 11-2 0V8a1 1 0 011-1zm0-2a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    Retarget
                    <InfoTip text={filters.retargeting
                        ? `Active — showing ${filteredCount.toLocaleString('en-US')} units. Click to turn off.`
                        : `${retargetCount.toLocaleString('en-US')} units have an active buyer quote within 10% of asking price, or already at/above AI estimated value. Call these buyers before reducing the listed price.`
                    } />
                </button>

            </div>
            {openTaskCount > 0 && (
                <div className="filter-secondary-row">
                    <button
                        className={`btn-qualifying-quote btn-open-tasks ${taskFilterActive ? 'active' : ''}`}
                        onClick={onTaskFilterToggle}
                    >
                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Open Tasks
                        <span className="task-filter-count">{openTaskCount}</span>
                    </button>
                </div>
            )}

            {/* Result count — sits above the table, aligned right. The one
                global "Clear all filters" lives here, next to the count it
                resets, and only while something is narrowing the table. */}
            <div className="result-count-row" ref={countRef} tabIndex={-1}>
                {hasActiveFilters && (
                    <button
                        type="button"
                        className="btn-clear-all-filters"
                        onClick={onClearAllFilters}
                        title="Clear all filters, anomaly signals and Open Tasks"
                    >
                        Clear all filters
                    </button>
                )}
                <span className="result-count">
                    <strong>{filteredCount.toLocaleString('en-US')}</strong> of {allData.length.toLocaleString('en-US')} units
                </span>
            </div>

            {/* Secondary filters (collapsible) */}
            {showMore && (
                <div className="filter-secondary">
                    <div className="filter-select aging-filter" ref={agingFilterRef}>
                        <label htmlFor="aging">Aging</label>
                        <button
                            ref={agingBtnRef}
                            id="aging"
                            type="button"
                            className={`aging-multiselect-btn ${selectedAging.length > 0 ? 'has-selection' : ''}`}
                            aria-haspopup="dialog"
                            aria-expanded={agingOpen}
                            onClick={() => setAgingOpen(open => !open)}
                        >
                            {selectedAging.length === 0
                                ? 'All Aging'
                                : selectedAging.length === 1
                                    ? (agingBuckets.find((b) => b.id === selectedAging[0])?.label || '1 selected')
                                    : `${selectedAging.length.toLocaleString('en-US')} selected`}
                            <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor" aria-hidden="true">
                                <path d="M6 8L1 3h10z" />
                            </svg>
                        </button>
                        {agingOpen && (
                            <div className="aging-multiselect-pop" role="group" aria-label="Filter by aging segment">
                                {selectedAging.length > 0 && (
                                    <div className="aging-multiselect-actions">
                                        <button
                                            type="button"
                                            className="aging-multiselect-action"
                                            onClick={() => handleChange('agingBucket', [])}
                                        >
                                            Clear aging
                                        </button>
                                    </div>
                                )}
                                {agingBuckets.map(opt => (
                                    <label key={opt.id} className="aging-multiselect-row">
                                        <input
                                            type="checkbox"
                                            checked={selectedAging.includes(opt.id)}
                                            onChange={() => toggleAging(opt.id)}
                                        />
                                        <span className="aging-multiselect-name">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="filter-select">
                        <label htmlFor="sellSpeed">Sell Speed</label>
                        <select id="sellSpeed" value={filters.sellSpeed || 'All'} onChange={(e) => handleChange('sellSpeed', e.target.value)}>
                            {sellSpeedOptions.map(opt => (
                                <option key={opt} value={opt}>{opt === 'All' ? 'All Speeds' : opt}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-select">
                        <label htmlFor="buyerHeat">Interest</label>
                        <select id="buyerHeat" value={filters.buyerHeat || 'All'} onChange={(e) => handleChange('buyerHeat', e.target.value)}>
                            {buyerHeatOptions.map(opt => (
                                <option key={opt} value={opt}>{opt === 'All' ? 'All Interest' : opt}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-select">
                        <label htmlFor="salesman">Salesman</label>
                        <select id="salesman" value={filters.salesman} onChange={(e) => handleChange('salesman', e.target.value)}>
                            {salesmen.map(opt => (
                                <option key={opt} value={opt}>{opt === 'All' ? 'All Salesmen' : opt}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Active filter pills */}
            {activeFilters.length > 0 && (
                <div className="filter-pills" ref={pillsRef}>
                    {activeFilters.map((f, index) => (
                        <span key={f.field} className="filter-pill">
                            <span className="pill-label">{f.label}:</span> {f.value}
                            <button
                                className="pill-remove"
                                onClick={() => removePill(f.field, index)}
                                aria-label={`Remove ${f.label} filter`}
                            >
                                <svg viewBox="0 0 20 20" fill="currentColor" width="10" height="10">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

FilterControls.propTypes = {
    filters: PropTypes.shape({
        location: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
        category: PropTypes.string.isRequired,
        newUsed: PropTypes.string.isRequired,
        agingBucket: PropTypes.arrayOf(PropTypes.number).isRequired,
        riskTier: PropTypes.string,
        sellSpeed: PropTypes.string.isRequired,
        buyerHeat: PropTypes.string.isRequired,
        salesman: PropTypes.string.isRequired,
        search: PropTypes.string.isRequired,
        qualifyingQuote: PropTypes.bool.isRequired,
        retargeting: PropTypes.bool.isRequired,
        makes: PropTypes.arrayOf(PropTypes.string),
        models: PropTypes.arrayOf(PropTypes.string),
        yearFrom: PropTypes.number,
        yearTo: PropTypes.number,
        includeNoYear: PropTypes.bool,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    allData: PropTypes.arrayOf(PropTypes.object).isRequired,
    filteredCount: PropTypes.number.isRequired,
    onQualifyingQuoteToggle: PropTypes.func.isRequired,
    onRetargetingToggle: PropTypes.func.isRequired,
    hasActiveFilters: PropTypes.bool,
    onClearAllFilters: PropTypes.func.isRequired,
    resultCountRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    taskFilterActive: PropTypes.bool,
    openTaskCount: PropTypes.number,
    onTaskFilterToggle: PropTypes.func,
    machineFacetData: PropTypes.arrayOf(PropTypes.object),
    machineCatalog: PropTypes.shape({
        makes: PropTypes.instanceOf(Map),
        models: PropTypes.instanceOf(Map),
        years: PropTypes.arrayOf(PropTypes.number),
    }),
    currentYear: PropTypes.number,
};

FilterControls.defaultProps = {
    hasActiveFilters: false,
    resultCountRef: undefined,
    taskFilterActive: false,
    openTaskCount: 0,
    onTaskFilterToggle: undefined,
    machineFacetData: undefined,
    machineCatalog: undefined,
    currentYear: undefined,
};

export default FilterControls;
